import { ApiResponse, PeriodData } from '../types/api';
import { TableData, TableColumn } from '../components/GenericTable';
import { ChartDataset } from '../components/GenericBarChart';
import { CurveDataset } from '../components/GenericCurveChart';

const extractDateFromPeriod = (period: PeriodData, data: ApiResponse): Date | null => {
  const dateStr = period.Description?.Value;
  
  if (!dateStr) return null;

  try {
    if (/(\d{2}\/\d{2}\/\d{4})/.test(dateStr)) {
      const [day, month, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day);
    }
    
    if (dateStr.includes('Año') && /\d{4}/.test(dateStr)) {
      const yearMatch = dateStr.match(/\d{4}/);
      if (yearMatch) {
        const year = parseInt(yearMatch[0]);
        
        const firstWeekOfYear = data.meteredPerPeriodLists.find(p => 
          p.ParentId === period.Id && p.Type === 2
        );
        
        if (firstWeekOfYear) {
          const firstDayOfWeek = data.meteredPerPeriodLists.find(p => 
            p.ParentId === firstWeekOfYear.Id && p.Type === 3
          );
          
          if (firstDayOfWeek && firstDayOfWeek.Description?.Value) {
            const dayDateStr = firstDayOfWeek.Description.Value;
            if (/(\d{2}\/\d{2}\/\d{4})/.test(dayDateStr)) {
              const [day, month, year] = dayDateStr.split('/').map(Number);
              return new Date(year, month - 1, day);
            }
          }
        }
        return new Date(year, 0, 1);
      }
    }
    
    if (dateStr.includes('Semana') && /\d+/.test(dateStr)) {
      const weekMatch = dateStr.match(/\d+/);
      if (weekMatch) {
        const firstDayOfWeek = data.meteredPerPeriodLists.find(p => 
          p.ParentId === period.Id && p.Type === 3
        );
        
        if (firstDayOfWeek && firstDayOfWeek.Description?.Value) {
          const dayDateStr = firstDayOfWeek.Description.Value;
          if (/(\d{2}\/\d{2}\/\d{4})/.test(dayDateStr)) {
            const [day, month, year] = dayDateStr.split('/').map(Number);
            return new Date(year, month - 1, day);
          }
        }
        
        if (period.CodPeriod && period.CodPeriod.length >= 6) {
          const year = parseInt(period.CodPeriod.substring(2, 6));
          const weekNumber = parseInt(weekMatch[0]);
          const firstDayOfYear = new Date(year, 0, 1);
          const daysToAdd = (weekNumber - 1) * 7;
          return new Date(firstDayOfYear.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
        }
      }
    }
    
    if (period.CodPeriod) {
      if (period.CodPeriod.length >= 6) {
        const year = parseInt(period.CodPeriod.substring(2, 6));
        if (!isNaN(year)) {
          return new Date(year, 0, 1);
        }
      }
    }
  } catch (error) {
    console.warn('Error parsing date for period:', period.CodPeriod, dateStr, error);
  }
  
  return null;
};

export const transformProgressTableData = (
  data: ApiResponse, 
  viewLevel: 'all' | 'year' | 'week' | 'day' = 'week',
  dateRange: { start: string; end: string } = { start: '', end: '' }
): TableData[] => {
  
  let filteredPeriods = data.meteredPerPeriodLists.filter(period => {
    switch (viewLevel) {
      case 'year':
        return period.Level === 2;
      case 'week':
        return period.Level === 3;
      case 'day':
        return period.Level === 4;
      case 'all':
      default:
        return true;
    }
  });

  if (dateRange.start && dateRange.end) {
    const parseDateFromDDMMYYYY = (dateStr: string): Date => {
      const [day, month, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day);
    };

    filteredPeriods = filteredPeriods.filter(period => {
      const periodDate = extractDateFromPeriod(period, data);
      if (!periodDate) return false;

      const startDate = parseDateFromDDMMYYYY(dateRange.start);
      const endDate = parseDateFromDDMMYYYY(dateRange.end);
      
      return periodDate >= startDate && periodDate <= endDate;
    });
  }

  const buildHierarchicalData = (items: PeriodData[]): TableData[] => {
    const itemsMap = new Map<number, TableData>();
    const rootItems: TableData[] = [];

    items.forEach(item => {
      itemsMap.set(item.Id, {
        id: item.Id.toString(),
        key: item.CodPeriod,
        Periodo: item.Description?.Value || '',
        Programado: item.Programmed?.Value || '0',
        Ejecutado: item.Executed?.Value || '0',
        Proyectado: item.Projected?.Value || '0',
        level: item.Level,
        children: [],
        _metadata: {
          backgroundColor: item.Description?.BackgroundColor || '',
          textColor: item.Description?.Color || '',
          programmedBgColor: item.Programmed?.BackgroundColor || '',
          programmedTextColor: item.Programmed?.Color || '',
          executedBgColor: item.Executed?.BackgroundColor || '',
          executedTextColor: item.Executed?.Color || '',
          projectedBgColor: item.Projected?.BackgroundColor || '',
          projectedTextColor: item.Projected?.Color || '',
          level: item.Level
        }
      });
    });

    items.forEach(item => {
      const hierarchicalItem = itemsMap.get(item.Id)!;
      
      if (item.ParentId === 0) {
        rootItems.push(hierarchicalItem);
      } else {
        const parent = itemsMap.get(item.ParentId);
        if (parent) {
          parent.children!.push(hierarchicalItem);
        } else {
          rootItems.push(hierarchicalItem);
        }
      }
    });
    return rootItems;
  };

  return buildHierarchicalData(filteredPeriods);
};

export const transformProgressChartData = (
  data: ApiResponse, 
  viewLevel: 'all' | 'year' | 'week' | 'day' = 'week',
  dateRange: { start: string; end: string } = { start: '', end: '' }
): {
  labels: string[];
  datasets: ChartDataset[];
  maxValue: number;
  shouldUseHorizontalLabels: boolean;
} => {
  
  let filteredItems = data.meteredPerPeriodLists.filter(period => {
    switch (viewLevel) {
      case 'year':
        return period.Level === 2;
      case 'week':
        return period.Level === 3;
      case 'day':
        return period.Level === 4;
      case 'all':
      default:
        return true;
    }
  });

  if (dateRange.start && dateRange.end) {
    const parseDateFromDDMMYYYY = (dateStr: string): Date => {
      const [day, month, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day);
    };

    filteredItems = filteredItems.filter(period => {
      const periodDate = extractDateFromPeriod(period, data);
      if (!periodDate) return false;

      const startDate = parseDateFromDDMMYYYY(dateRange.start);
      const endDate = parseDateFromDDMMYYYY(dateRange.end);
      
      return periodDate >= startDate && periodDate <= endDate;
    });
  }

  const itemCount = filteredItems.length;
  const shouldUseHorizontalLabels = itemCount <= 12;

  let calculatedMaxValue = 0;
  
  filteredItems.forEach(item => {
    const programmed = parseFloat(item.Programmed?.Value || '0');
    const projected = parseFloat(item.Projected?.Value || '0');
    const executed = parseFloat(item.Executed?.Value || '0');
    const totalProgrammedProjected = programmed + projected;
    
    calculatedMaxValue = Math.max(calculatedMaxValue, programmed, projected, executed, totalProgrammedProjected);
  });

  if (calculatedMaxValue === 0) {
    calculatedMaxValue = 100;
  } else {
    calculatedMaxValue = Math.floor(calculatedMaxValue * 1.01);
  }

  const labels = filteredItems.map(item => item.Description.Value);
  
  const datasets: ChartDataset[] = [
    {
      label: 'Programado + Proyectado',
      data: filteredItems.map(item => {
        const programmed = parseFloat(item.Programmed?.Value || '0');
        const projected = parseFloat(item.Projected?.Value || '0');
        return programmed + projected;
      }),
      backgroundColor: 'rgba(162, 198, 230, 0.8)',
      borderColor: 'rgba(150, 190, 235, 1)',
      borderWidth: 2,
    },
    {
      label: 'Ejecutado',
      data: filteredItems.map(item => parseFloat(item.Executed?.Value || '0')),
      backgroundColor: 'rgba(75, 192, 192, 0.8)',
      borderColor: 'rgba(75, 192, 192, 1)',
      borderWidth: 2,
    },
  ];

  return {
    labels,
    datasets,
    maxValue: calculatedMaxValue,
    shouldUseHorizontalLabels
  };
};

export const transformProgressCurveData = (
  data: ApiResponse,
  viewLevel: 'all' | 'year' | 'week' | 'day' = 'week',
  dateRange: { start: string; end: string } = { start: '', end: '' }
): {
  labels: string[];
  datasets: CurveDataset[];
  maxValue: number;
  shouldUseHorizontalLabels: boolean;
} => {
  
  let filteredItems = data.meteredPerPeriodLists.filter(period => {
    switch (viewLevel) {
      case 'year':
        return period.Level === 2;
      case 'week':
        return period.Level === 3;
      case 'day':
        return period.Level === 4;
      case 'all':
      default:
        return true;
    }
  });

  if (dateRange.start && dateRange.end) {
    const parseDateFromDDMMYYYY = (dateStr: string): Date => {
      const [day, month, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day);
    };

    filteredItems = filteredItems.filter(period => {
      const periodDate = extractDateFromPeriod(period, data);
      if (!periodDate) return false;

      const startDate = parseDateFromDDMMYYYY(dateRange.start);
      const endDate = parseDateFromDDMMYYYY(dateRange.end);
      
      return periodDate >= startDate && periodDate <= endDate;
    });
  }

  const itemCount = filteredItems.length;
  const shouldUseHorizontalLabels = itemCount <= 12;

  let calculatedMaxValue = 0;
  
  filteredItems.forEach(item => {
    const programmedProjectedCumulative = parseFloat(item.ProgrammedProjectedCumulative || '0');
    const executedCumulative = parseFloat(item.ExecutedCumulative || '0');
    
    calculatedMaxValue = Math.max(calculatedMaxValue, programmedProjectedCumulative, executedCumulative);
  });

  if (calculatedMaxValue === 0) {
    calculatedMaxValue = 100;
  } else {
    calculatedMaxValue = Math.floor(calculatedMaxValue * 1.1);
  }

  const labels = filteredItems.map(item => item.Description.Value);
  
  const datasets: CurveDataset[] = [
    {
      label: 'Programado + Proyectado',
      data: filteredItems.map(item => parseFloat(item.ProgrammedProjectedCumulative || '0')),
      borderColor: 'rgba(162, 198, 230, 1)',
      backgroundColor: 'rgba(162, 198, 230, 0.2)',
      borderWidth: 2,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      fill: false,
    },
    {
      label: 'Ejecutado',
      data: filteredItems.map(item => parseFloat(item.ExecutedCumulative || '0')),
      borderColor: 'rgba(75, 192, 192, 1)',
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      borderWidth: 2,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      fill: false,
    }
  ];

  return {
    labels,
    datasets,
    maxValue: calculatedMaxValue,
    shouldUseHorizontalLabels
  };
};

export const progressTableColumns: TableColumn[] = [
  { key: 'Periodo', label: 'Periodo', width: '40%' },
  { key: 'Programado', label: 'Programado', align: 'right', width: '20%' },
  { key: 'Ejecutado', label: 'Ejecutado', align: 'right', width: '20%' },
  { key: 'Proyectado', label: 'Proyectado', align: 'right', width: '20%' }
];

export const getProgressRowBackgroundColor = (row: TableData) => {
  return row._metadata?.backgroundColor || '#ffffff';
};

export const getProgressRowTextColor = (row: TableData) => {
  return row._metadata?.textColor || '#000000';
};

export const getProgressCellBackgroundColor = (row: TableData, columnKey: string) => {
  const metadata = row._metadata;
  if (!metadata) return getProgressRowBackgroundColor(row);
  
  switch(columnKey) {
    case 'Programado':
      return metadata.programmedBgColor || getProgressRowBackgroundColor(row);
    case 'Ejecutado':
      return metadata.executedBgColor || getProgressRowBackgroundColor(row);
    case 'Proyectado':
      return metadata.projectedBgColor || getProgressRowBackgroundColor(row);
    default:
      return getProgressRowBackgroundColor(row);
  }
};

export const getProgressCellTextColor = (row: TableData, columnKey: string) => {
  const metadata = row._metadata;
  if (!metadata) return getProgressRowTextColor(row);
  
  switch(columnKey) {
    case 'Programado':
      return metadata.programmedTextColor || getProgressRowTextColor(row);
    case 'Ejecutado':
      return metadata.executedTextColor || getProgressRowTextColor(row);
    case 'Proyectado':
      return metadata.projectedTextColor || getProgressRowTextColor(row);
    default:
      return getProgressRowTextColor(row);
  }
};